(() => {
  'use strict';

  const CONTROL_SELECTOR = '[data-wagnudge-privacy-preferences]';
  const DATA_SHARING_SELECTOR = '[data-wagnudge-data-sharing-opt-out]';
  const SHOPIFY_PRIVACY_BANNER_SOURCE =
    'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';

  let loaderPromise;
  let lastTrigger = null;

  const getPrivacyBanner = () => {
    const banner = window.privacyBanner;
    return banner && typeof banner.showPreferences === 'function' ? banner : null;
  };

  const nativePreferencesAvailable = () => {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (
      !customerPrivacy ||
      typeof customerPrivacy.shouldShowBanner !== 'function'
    ) return false;

    try {
      /* Shopify's regional banner signal is the only supported storefront
       * indication that the native preferences UI is configured for this
       * visitor. Historical consent values are not configuration evidence:
       * they can outlive a merchant-side region change and previously caused
       * an enabled control whose native call resolved without opening a UI. */
      return customerPrivacy.shouldShowBanner() === true;
    } catch (_) {
      return false;
    }
  };

  const nativePreferencesDialogIsOpen = () => {
    const dialog = document.querySelector('#shopify-pc__prefs__dialog');
    if (!dialog) return false;

    const styles = window.getComputedStyle(dialog);
    const bounds = dialog.getBoundingClientRect();
    return styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      bounds.width > 0 &&
      bounds.height > 0;
  };

  const disableControl = (control) => {
    delete control.dataset.wagnudgePrivacyReady;
    control.hidden = true;
  };

  const loadPrivacyBanner = () => {
    const available = getPrivacyBanner();
    if (available) return Promise.resolve(available);
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        `script[src="${SHOPIFY_PRIVACY_BANNER_SOURCE}"]`,
      );

      const resolveIfAvailable = () => {
        const banner = getPrivacyBanner();
        if (banner) resolve(banner);
        else reject(new Error('Shopify privacy preferences API unavailable'));
      };

      if (existing) {
        if (getPrivacyBanner()) resolve(getPrivacyBanner());
        else {
          existing.addEventListener('load', resolveIfAvailable, { once: true });
          existing.addEventListener('error', reject, { once: true });
        }
        return;
      }

      const script = document.createElement('script');
      script.src = SHOPIFY_PRIVACY_BANNER_SOURCE;
      script.async = true;
      script.dataset.wagnudgeShopifyPrivacyBanner = 'true';
      script.addEventListener('load', resolveIfAvailable, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return loaderPromise;
  };

  const bindControl = (control, banner) => {
    if (control.dataset.wagnudgePrivacyReady === 'true') return;

    if (!banner || !nativePreferencesAvailable()) {
      disableControl(control);
      return;
    }

    control.dataset.wagnudgePrivacyReady = 'true';
    control.hidden = false;
    control.addEventListener('click', () => {
      const currentBanner = getPrivacyBanner();
      if (!currentBanner || !nativePreferencesAvailable()) {
        disableControl(control);
        return;
      }

      lastTrigger = control;
      try {
        Promise.resolve(window.privacyBanner.showPreferences())
          .then(() => {
            if (!nativePreferencesDialogIsOpen()) disableControl(control);
          })
          .catch(() => disableControl(control));
      } catch (_) {
        disableControl(control);
      }
    });
  };

  const initialize = (root = document) => {
    const controls = Array.from(root.querySelectorAll(CONTROL_SELECTOR));
    const dataSharingLinks = Array.from(root.querySelectorAll(DATA_SHARING_SELECTOR));
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    dataSharingLinks.forEach((link) => {
      let eligible = false;
      try {
        eligible = Boolean(
          customerPrivacy &&
          typeof customerPrivacy.saleOfDataRegion === 'function' &&
          customerPrivacy.saleOfDataRegion()
        );
      } catch (_) {
        eligible = false;
      }
      link.hidden = !eligible;
    });
    if (!controls.length) return;

    loadPrivacyBanner()
      .then((banner) => controls.forEach((control) => bindControl(control, banner)))
      .catch(() => controls.forEach((control) => disableControl(control)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => initialize());
  document.addEventListener('visitorConsentCollected', () => {
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  });
})();
